import { useEffect, useState, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { Link } from "react-router-dom";
import {
    AlertCircle,
    LayoutDashboard,
    BarChart2,
    PieChart as PieChartIcon,
    Receipt,
    Calculator,
    ClipboardList,
    FolderOpen,
    ChevronRight,
    Users,
    ExternalLink,
    X,
    Sparkles,
    TrendingUp
} from "lucide-react";
import toast from "react-hot-toast";

import DistroList from "../../components/distros-list/distros-list.component";
import { formatDate } from "../../components/distribution/utils";
import PageWrapper from "../../components/page-wrapper/page-wrapper.components";
import { ownerSidebarData } from "../../components/sidebar/sidebarData";
import MiniDonutChart from "../../components/ui/MiniDonutChart";
import HorizontalBarList from "../../components/ui/HorizontalBarList";
import MiniBarList from "../../components/ui/MiniBarList";
import { setCurrentDistro } from "../../redux/distro/distro.actions";
import { selectCurrentDistro } from "../../redux/distro/distro.selector";
import { selectCurrentUser } from "../../redux/user/user.selectors";
import { sessionExpired, updateToken } from "../../redux/user/user.actions";
import { authFetch, authPost, getApiUrl, SessionExpiredError } from "../../utils/api";
import { isClientOnlyForDistro, withoutRestrictedLinks } from "../../utils/firmAccess";

const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const quickLinks = [
    { to: '/user/financials', icon: BarChart2, title: 'Financials' },
    { to: '/user/depositsummary', icon: Receipt, title: 'Deposits' },
    { to: '/user/quotes', icon: Calculator, title: 'Quotes' },
    { to: '/user/enrollments', icon: ClipboardList, title: 'Enrollments' },
    { to: '/user/marketing', icon: FolderOpen, title: 'Documents' },
];

const COLORS = {
    spending: '#1e85e5',
    corehealth: '#8B46D0',
    teladoc: '#00BBB2'
};

const LABELS = {
    spending: 'Spending',
    corehealth: 'CoreHealth',
    teladoc: 'Teladoc'
};

const DashboardPage = ({currentUser, currentDistro, setCurrentDistro, onSessionExpired, onTokenRefresh}) => {
    const [userInfo, setUserInfo] = useState({});
    const [financialInfo, setFinancialInfo] = useState({
        commission: [],
        summary: [],
    });
    const [advisorData, setAdvisorData] = useState([]);
    const [depositData, setDepositData] = useState({ total: 0, count: 0 });
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [clientsViewMode, setClientsViewMode] = useState('bar');
    const [advisorsViewMode, setAdvisorsViewMode] = useState('bar');
    const [loadingError, setLoadingError] = useState(false);
    const toastIdRef = useRef(null);
    const apiUrl = getApiUrl();
    const clientOnly = isClientOnlyForDistro(currentUser, currentDistro);
    const visibleQuickLinks = clientOnly ? withoutRestrictedLinks(quickLinks) : quickLinks;

    // auth params for authFetch
    const authParams = { currentUser, onSessionExpired, onTokenRefresh };

    const fetchData = useCallback(async (distroId) => {
        try {
            const response = await authFetch(
                `${apiUrl}/users/${currentUser.id}/${distroId}`,
                {},
                authParams
            );
            if (response.ok) {
                const data = await response.json();
                setUserInfo(data);
            } else {
                setLoadingError(true);
            }
        } catch (err) {
            if (!(err instanceof SessionExpiredError)) {
                setLoadingError(true);
            }
        }
    }, [apiUrl, currentUser, authParams]);

    const fetchFinancialData = useCallback(async (startingDate, endingDate) => {
        try {
            const response = await authFetch(
                `${apiUrl}/financials/${currentDistro}/${formatDate(startingDate)}/${formatDate(endingDate)}`,
                {},
                authParams
            );
            if (response.ok) {
                const data = await response.json();
                setFinancialInfo(data);
            }
        } catch (err) {
            // session expired is handled by authFetch
            if (!(err instanceof SessionExpiredError)) {
                console.error('Error fetching financial data:', err);
            }
        }
    }, [apiUrl, currentDistro, authParams]);

    const fetchAdvisorData = useCallback(async (startingDate, endingDate) => {
        try {
            // format dates locally to avoid UTC timezone issues
            const formatLocalDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };
            const url = `${apiUrl}/distribution/${currentDistro}/advisor/${formatLocalDate(startingDate)}/${formatLocalDate(endingDate)}/all`;
            const response = await authFetch(url, {}, authParams);
            if (response.ok) {
                const data = await response.json();
                const sorted = (data.distributionSummaryList || [])
                    .sort((a, b) => b.total - a.total);
                setAdvisorData(sorted);
            }
        } catch (err) {
            // silently fail - not critical (unless session expired, which is handled)
        }
    }, [apiUrl, currentDistro, authParams]);

    const fetchDepositData = useCallback(async (startingDate, endingDate) => {
        try {
            // format dates locally to avoid UTC timezone issues
            const formatLocalDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };
            const params = new URLSearchParams({
                distroId: currentDistro,
                dateFrom: formatLocalDate(startingDate),
                dateTo: formatLocalDate(endingDate),
                selectedType: 'separated'
            });
            const response = await authFetch(
                `${apiUrl}/financials/getdepositsummary?${params}`,
                {},
                authParams
            );
            if (response.ok) {
                const data = await response.json();
                const total = (data.results || []).reduce((sum, d) => sum + Number(d.amount), 0);
                setDepositData({ total, count: (data.results || []).length });
            }
        } catch (err) {
            // silently fail - not critical (unless session expired, which is handled)
        }
    }, [apiUrl, currentDistro, authParams]);

    const updateUserWelcomePopup = useCallback(async () => {
        setUserInfo(prev => ({ ...prev, hasWelcomePopup: false }));
        try {
            await authPost(
                `${apiUrl}/users/updateuserwelcomepopup/${currentUser.id}`,
                {},
                authParams
            );
        } catch (err) {
            if (!(err instanceof SessionExpiredError)) {
                console.error('Failed to update welcome popup:', err);
            }
        }
    }, [apiUrl, currentUser, authParams]);

    useEffect(() => {
        if (currentUser.distros.length === 1) {
            setCurrentDistro(currentUser.distros[0].id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const loadDashboard = async () => {
            if (currentDistro !== null) {
                toastIdRef.current = toast.loading('Loading dashboard...');

                // calculate last month dates
                let defaultEndDate = new Date();
                defaultEndDate.setDate(1);
                defaultEndDate.setHours(-1);
                let defaultStartDate = new Date(defaultEndDate.getFullYear(), defaultEndDate.getMonth(), 1);

                // fetch all data in parallel
                await Promise.all(clientOnly ? [
                    fetchData(currentDistro)
                ] : [
                    fetchData(currentDistro),
                    fetchFinancialData(defaultStartDate, defaultEndDate),
                    fetchAdvisorData(defaultStartDate, defaultEndDate),
                    fetchDepositData(defaultStartDate, defaultEndDate)
                ]);

                toast.dismiss(toastIdRef.current);
                setIsInitialLoad(false);
            }
        };
        loadDashboard();
        return () => {
            if (toastIdRef.current) {
                toast.dismiss(toastIdRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDistro, clientOnly]);

    const handleClick = (e) => {
        setCurrentDistro(e.target.dataset.id);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount);
    };

    const totalAmount = financialInfo.commission.length > 0
        ? financialInfo.commission.reduce((sum, c) => sum + Number(c.amount), 0)
        : 0;

    const getSummaryAmount = (type) => {
        const item = financialInfo.summary.find(s =>
            s.type.toLowerCase().includes(type.toLowerCase())
        );
        return item ? parseFloat(item.amount) : 0;
    };

    // prepare summary data for charts
    const summaryData = [
        { type: 'spending', value: getSummaryAmount('spending'), label: 'Spending' },
        { type: 'corehealth', value: getSummaryAmount('corehealth'), label: 'CoreHealth' },
        { type: 'teladoc', value: getSummaryAmount('teladoc'), label: 'Teladoc' },
    ].filter(d => d.value > 0);

    const today = new Date();
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastMonthLabel = `${months[lastMonth.getMonth()]} ${lastMonth.getFullYear()}`;

    return (
        <>
            {currentDistro === null ? (
                <DistroList distros={currentUser.distros} handleClick={handleClick}/>
            ) : loadingError ? (
                <div className="flex items-center justify-center min-h-screen p-4">
                    <div className="bg-white rounded-2xl shadow-card p-8 max-w-md text-center">
                        <div className="flex justify-center mb-4">
                            <AlertCircle className="w-12 h-12 text-red-500" />
                        </div>
                        <h1 className="text-xl font-bold text-gray-900 mb-2">
                            Something went wrong
                        </h1>
                        <p className="text-gray-600 mb-6">
                            An error occurred while retrieving data. Please refresh the page and try again.
                        </p>
                        <a
                            href="/"
                            className="inline-flex items-center justify-center px-6 py-3 bg-primary text-white rounded-full font-semibold hover:bg-primary-dark transition-colors"
                        >
                            Return to Sign In
                        </a>
                    </div>
                </div>
            ) : (
                <PageWrapper sidebarData={ownerSidebarData}>
                    <div className="flex-1 px-6 md:px-12 lg:pl-12 lg:pr-24 py-6 pb-5 overflow-y-auto">
                        {/* page header */}
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 bg-primary/10 rounded-xl">
                                <LayoutDashboard className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                                    Dashboard
                                </h1>
                                <p className="text-sm text-gray-500">
                                    {`${months[today.getMonth()]} ${today.getFullYear()}`}
                                </p>
                            </div>
                        </div>

                        {/* ROW 1: Earnings + Top Clients */}
                        {!clientOnly && !isInitialLoad && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
                                {/* Total Earnings - 1/4 width on lg */}
                                <div className="bg-white rounded-2xl shadow-card p-4 sm:p-6">
                                    <p className="text-sm text-gray-500 mb-1">Total Earnings</p>
                                    <p className="text-xs text-gray-400 mb-3">{lastMonthLabel}</p>
                                    <p className="text-3xl font-bold text-gray-900 mb-4">
                                        {formatCurrency(totalAmount)}
                                    </p>

                                    {/* mini donut chart when multiple types */}
                                    {summaryData.length > 1 && (
                                        <div className="mb-4">
                                            <MiniDonutChart
                                                data={summaryData}
                                                total={totalAmount}
                                                formatCurrency={formatCurrency}
                                                size="small"
                                                showCenter={false}
                                                showLegend={false}
                                            />
                                        </div>
                                    )}

                                    {/* breakdown pills */}
                                    {summaryData.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5">
                                            {summaryData.map(item => (
                                                <span
                                                    key={item.type}
                                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium"
                                                    style={{
                                                        backgroundColor: `${COLORS[item.type]}15`,
                                                        color: COLORS[item.type]
                                                    }}
                                                >
                                                    <span
                                                        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                                        style={{ backgroundColor: COLORS[item.type] }}
                                                    />
                                                    {LABELS[item.type]} {formatCurrency(item.value)}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Top Clients - spans remaining width */}
                                <div className="sm:col-span-2 lg:col-span-3 bg-white rounded-2xl shadow-card p-4 sm:p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2">
                                            <h2 className="text-lg font-semibold text-gray-900">Top Clients</h2>
                                            <span className="text-sm text-gray-400">·</span>
                                            <span className="text-sm text-gray-400">{lastMonthLabel}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            {/* toggle */}
                                            <div className="flex items-center gap-0.5 p-0.5 bg-gray-100 rounded-full">
                                                <button
                                                    type="button"
                                                    onClick={() => setClientsViewMode('bar')}
                                                    className={`p-1 rounded-full transition-colors ${
                                                        clientsViewMode === 'bar'
                                                            ? 'bg-white shadow-sm text-gray-700'
                                                            : 'text-gray-400 hover:text-gray-600'
                                                    }`}
                                                >
                                                    <BarChart2 className="w-3 h-3" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setClientsViewMode('pie')}
                                                    className={`p-1 rounded-full transition-colors ${
                                                        clientsViewMode === 'pie'
                                                            ? 'bg-white shadow-sm text-gray-700'
                                                            : 'text-gray-400 hover:text-gray-600'
                                                    }`}
                                                >
                                                    <PieChartIcon className="w-3 h-3" />
                                                </button>
                                            </div>
                                            <Link
                                                to="/user/clients?type=policy"
                                                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                                                title="View All Clients"
                                            >
                                                <ExternalLink className="w-4 h-4 text-gray-400" />
                                            </Link>
                                        </div>
                                    </div>
                                    <HorizontalBarList
                                        data={financialInfo.commission}
                                        maxItems={5}
                                        formatCurrency={formatCurrency}
                                        viewMode={clientsViewMode}
                                    />
                                </div>
                            </div>
                        )}

                        {/* ROW 2: Quick Summaries */}
                        {!clientOnly && !isInitialLoad && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6">
                                {/* Top Advisors */}
                                <div className="bg-white rounded-2xl shadow-card p-4 sm:p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2">
                                            <div className="p-1.5 bg-primary/10 rounded-lg">
                                                <Users className="w-4 h-4 text-primary" />
                                            </div>
                                            <h2 className="text-base font-semibold text-gray-900">Top Advisors</h2>
                                            <span className="text-xs text-gray-400">{lastMonthLabel}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            {/* toggle */}
                                            <div className="flex items-center gap-0.5 p-0.5 bg-gray-100 rounded-full">
                                                <button
                                                    type="button"
                                                    onClick={() => setAdvisorsViewMode('bar')}
                                                    className={`p-1 rounded-full transition-colors ${
                                                        advisorsViewMode === 'bar'
                                                            ? 'bg-white shadow-sm text-gray-700'
                                                            : 'text-gray-400 hover:text-gray-600'
                                                    }`}
                                                >
                                                    <BarChart2 className="w-3 h-3" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setAdvisorsViewMode('pie')}
                                                    className={`p-1 rounded-full transition-colors ${
                                                        advisorsViewMode === 'pie'
                                                            ? 'bg-white shadow-sm text-gray-700'
                                                            : 'text-gray-400 hover:text-gray-600'
                                                    }`}
                                                >
                                                    <PieChartIcon className="w-3 h-3" />
                                                </button>
                                            </div>
                                            <Link
                                                to="/user/financials"
                                                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                                                title="View Financials"
                                            >
                                                <ExternalLink className="w-4 h-4 text-gray-400" />
                                            </Link>
                                        </div>
                                    </div>
                                    {advisorData.length > 0 ? (
                                        <MiniBarList
                                            data={advisorData.map(a => ({
                                                name: a.agentReportingCode || 'Unknown',
                                                amount: a.total
                                            }))}
                                            maxItems={3}
                                            formatCurrency={formatCurrency}
                                            color="primary"
                                            viewMode={advisorsViewMode}
                                        />
                                    ) : (
                                        <p className="text-sm text-gray-400 text-center py-4">No advisor data available</p>
                                    )}
                                </div>

                                {/* Deposits */}
                                <div className="bg-white rounded-2xl shadow-card p-4 sm:p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2">
                                            <div className="p-1.5 bg-success/10 rounded-lg">
                                                <Receipt className="w-4 h-4 text-success" />
                                            </div>
                                            <h2 className="text-base font-semibold text-gray-900">Deposits</h2>
                                            <span className="text-xs text-gray-400">{lastMonthLabel}</span>
                                        </div>
                                        <Link
                                            to="/user/depositsummary"
                                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                                            title="View Deposits"
                                        >
                                            <ExternalLink className="w-4 h-4 text-gray-400" />
                                        </Link>
                                    </div>
                                    <p className="text-2xl font-bold text-gray-900 mb-1">
                                        {formatCurrency(depositData.total)}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        {depositData.count} deposit{depositData.count !== 1 ? 's' : ''}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* quick links */}
                        <div className="bg-white rounded-2xl shadow-card p-4">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 block">Quick Access</span>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                                {visibleQuickLinks.map(link => (
                                    <Link
                                        key={link.to}
                                        to={link.to}
                                        className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-gray-50 hover:bg-primary/10 text-gray-700 hover:text-primary text-sm font-medium transition-colors"
                                    >
                                        <link.icon className="w-4 h-4 flex-shrink-0" />
                                        <span className="truncate">{link.title}</span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* welcome popup */}
                    {!isInitialLoad && userInfo.hasWelcomePopup && !currentUser?.opsActor &&
                        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                            <div className="bg-white rounded-2xl shadow-modal w-full max-w-md overflow-hidden">
                                {/* header accent */}
                                <div className="bg-gradient-to-br from-primary to-primary-dark px-6 pt-8 pb-6 text-center relative">
                                    <button
                                        onClick={updateUserWelcomePopup}
                                        className="absolute top-3 right-3 p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                                    >
                                        <X className="w-4 h-4 text-white/70" />
                                    </button>
                                    <div className="inline-flex items-center justify-center w-14 h-14 bg-white/20 rounded-2xl mb-4">
                                        <Sparkles className="w-7 h-7 text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-1">Welcome!</h3>
                                    <p className="text-sm text-white/80">Your Advisor Portal has a new look</p>
                                </div>

                                <div className="px-6 py-5">
                                    <p className="text-sm text-gray-600 mb-4">
                                        A refreshed design with the same features and functions you rely on.
                                    </p>

                                    {currentDistro &&
                                        <div className="space-y-3 mb-5">
                                            <div className="flex items-start gap-3 p-3 bg-primary/5 rounded-xl">
                                                <div className="p-1.5 bg-primary/10 rounded-lg mt-0.5">
                                                    <LayoutDashboard className="w-4 h-4 text-primary" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">Dashboard Insights</p>
                                                    <p className="text-xs text-gray-500 mt-0.5">Check out your growing dashboard with key metrics at a glance</p>
                                                </div>
                                            </div>
                                            {!clientOnly && (
                                                <div className="flex items-start gap-3 p-3 bg-success/5 rounded-xl">
                                                    <div className="p-1.5 bg-success/10 rounded-lg mt-0.5">
                                                        <TrendingUp className="w-4 h-4 text-success" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900">Streamlined Reporting</p>
                                                        <p className="text-xs text-gray-500 mt-0.5">Everything you need is now in one Financials tab</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    }

                                    <button
                                        onClick={updateUserWelcomePopup}
                                        className="w-full bg-primary text-white py-3 px-6 rounded-full font-semibold text-sm hover:bg-primary-dark active:scale-[0.98] transition-all duration-200"
                                    >
                                        Got it
                                    </button>
                                </div>
                            </div>
                        </div>
                    }
                </PageWrapper>
            )}
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    currentUser: selectCurrentUser,
    currentDistro: selectCurrentDistro,
});

const mapDispatchToProps = (dispatch) => ({
    setCurrentDistro: (data) => dispatch(setCurrentDistro(data)),
    onSessionExpired: () => dispatch(sessionExpired()),
    onTokenRefresh: (token) => dispatch(updateToken(token)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DashboardPage);
